import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { isUnstableSignal } from "@opus-dt/shared-types";
import { Button } from "./ui/button";
import { useAlarms, useAcknowledgeAlarm } from "@/features/alarms/hooks";
import { formatRelativeTime } from "@/lib/display-labels";
import { cn } from "@/lib/utils";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { Notify } from "@/components/ui/notify";
import { AlarmReliabilityBadge, AlarmSeverityBadge } from "./AlarmBadge";

export function NotificationBell() {
  const navigate = useNavigate();
  const { data: alarms = [] } = useAlarms({});
  const ack = useAcknowledgeAlarm();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const unack = alarms.filter((a) => a.state === "UNACKNOWLEDGED");
  const critical = unack.filter((a) => a.severity === "HIGH").length;
  // Fontes instáveis (oscilando / confiabilidade suspeita) ativas.
  const unstable = unack.filter((a) => isUnstableSignal(a)).length;

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function quickAck(id: string) {
    try {
      await ack.mutateAsync({ id, data: { cause: "Reconhecido via NotificationBell" } });
    } catch (err) {
      Notify.toast({
        variant: "danger",
        title: "Falha ao reconhecer alarme",
        description: getFriendlyErrorMessage(err),
      });
    }
  }

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notificações — ${unack.length} pendente(s)`}
        className="relative"
      >
        <Bell className="h-4 w-4" />
        {unack.length > 0 ? (
          <span
            className={cn(
              "absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-semibold",
              critical > 0
                ? "animate-pulse-dot bg-destructive text-destructive-foreground"
                : "bg-warning text-warning-foreground"
            )}
          >
            {unack.length}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div className="absolute right-0 top-12 z-50 w-80 animate-scale-in rounded-lg border bg-card shadow-lg">
          <header className="border-b p-3">
            <h3 className="text-sm font-semibold">Alarmes pendentes</h3>
            <p className="text-xs text-muted-foreground">
              {unack.length} não reconhecido(s) — {critical} crítico(s)
              {unstable > 0 ? ` · ${unstable} fonte(s) instável(is)` : ""}.
            </p>
          </header>
          <div className="max-h-80 overflow-y-auto">
            {unack.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">Sem alarmes pendentes. 🎉</p>
            ) : (
              unack.slice(0, 8).map((a) => (
                <div key={a.id} className="border-b p-3 last:border-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{a.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {a.machine.code} · {formatRelativeTime(a.startAt)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <AlarmSeverityBadge severity={a.severity} />
                      <AlarmReliabilityBadge reliability={a.reliability} />
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void quickAck(a.id)}
                      disabled={ack.isPending}
                    >
                      Reconhecer rápido
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          <footer className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                setOpen(false);
                navigate("/alarms");
              }}
            >
              Ver todos
            </Button>
          </footer>
        </div>
      ) : null}
    </div>
  );
}
